import { useEffect, useState } from "react";
import { AlertCircle, Check, Clock, Download, Phone, Star } from "lucide-react";

/**
 * Öffentliche Auftragsverfolgung: /status?anfrage=TOKEN
 *
 * Diese Seite ist bewusst OHNE Anmeldung erreichbar - der Kunde hat kein
 * Konto und soll nur seinen eigenen Vorgang sehen. Der lange Token in der
 * Adresse ist der Schlüssel; er taucht in jeder Bestätigungsmail auf.
 */

type Stufe = { id: string; label: string; beschreibung: string };

/**
 * ALLE zehn Status der Datenbank, in der Reihenfolge des Kanban.
 *
 * Die Liste muss vollständig bleiben: fehlt ein Status, findet die Seite ihn
 * nicht und springt zurück auf den ersten Schritt. Genau das passierte, als
 * "inspection_done", "in_progress" und "invoiced" fehlten - ab der
 * Besichtigung stand die Anzeige still.
 */
const STUFEN: Stufe[] = [
  { id: "received", label: "Anfrage eingegangen", beschreibung: "Wir haben Ihre Anfrage erhalten und melden uns." },
  { id: "in_review", label: "In Prüfung", beschreibung: "Wir schauen uns Ihr Anliegen gerade an." },
  { id: "inspection_scheduled", label: "Termin vor Ort", beschreibung: "Ein Termin zur Besichtigung steht." },
  { id: "inspection_done", label: "Besichtigung erledigt", beschreibung: "Wir waren bei Ihnen und erstellen nun das Angebot." },
  { id: "quote_sent", label: "Angebot verschickt", beschreibung: "Sie haben ein Angebot von uns bekommen." },
  { id: "quote_approved", label: "Auftrag erteilt", beschreibung: "Vielen Dank für Ihren Auftrag." },
  { id: "scheduled", label: "Ausführung geplant", beschreibung: "Der Termin für die Arbeiten steht fest." },
  { id: "in_progress", label: "In Arbeit", beschreibung: "Wir sind gerade für Sie im Einsatz." },
  { id: "completed", label: "Abgeschlossen", beschreibung: "Die Arbeiten sind erledigt." },
  { id: "invoiced", label: "Rechnung gestellt", beschreibung: "Die Rechnung ist unterwegs." },
  { id: "paid", label: "Bezahlt", beschreibung: "Alles erledigt - vielen Dank!" },
];

interface Antwort {
  anfrage: {
    referenceNumber: string;
    status: string;
    serviceCategory: string;
    subService: string | null;
    description: string | null;
    customerVorname: string;
    customerStrasse: string | null;
    customerPlz: string | null;
    inspectionDate: string | null;
    scheduledDate: string | null;
    assignedTechnician: string | null;
    createdAt: string;
  };
  company: { name: string; phone: string | null; primary_color: string; greeting_style: string; bewertung_link?: string | null };
  termine?: Termin[];
  dokumente?: Dokument[];
  documents?: HochgeladenesDokument[];
  rating?: { stars: number; comment: string | null; created_at: string } | null;
}

/** Vom Betrieb hochgeladene PDF-Dateien (Angebot, Arbeitsbericht, Rechnung). */
interface HochgeladenesDokument {
  id: string;
  doc_type: "angebot" | "arbeitsbericht" | "rechnung";
  filename: string;
  created_at: string;
}

const DOKUMENT_NAME: Record<string, string> = {
  angebot: "Angebot",
  arbeitsbericht: "Arbeitsbericht",
  rechnung: "Rechnung",
};

interface Termin {
  id: string; art: string; beginnt_am: string; dauer_minuten: number; gewaehlt: boolean;
  abgesagt_am?: string | null;
}

interface Position {
  bezeichnung: string; menge: number; einheit: string; einzelpreisCent: number;
}

interface Dokument {
  id: string; art: "angebot" | "arbeitsbericht" | "rechnung"; nummer: string; titel: string;
  positionen: Position[]; netto_cent: number; mwst_prozent: number; brutto_cent: number;
  text_oben: string | null; text_unten: string | null; gueltig_bis: string | null;
  faellig_am: string | null;
  storniert_am: string | null; storno_zu: string | null;
  angenommen_am: string | null; abgelehnt_am: string | null; created_at: string;
}

const EUR = (cent: number) =>
  (cent / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

function datum(s: string | null): string {
  if (!s) return "";
  return new Date(s).toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export function Status() {
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  // Anders als "fehler": die Seite bleibt stehen, oben erscheint ein Hinweis.
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const token = new URLSearchParams(window.location.search).get("anfrage") ?? "";

  function laden() {
    fetch(`/v1/status/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("nicht gefunden"))))
      .then(setDaten)
      .catch(() =>
        setFehler("Zu diesem Link finden wir keinen Vorgang. Vielleicht ist er nicht mehr gültig."),
      );
  }

  useEffect(() => {
    if (!token) {
      setFehler("In der Adresse fehlt die Vorgangsnummer. Bitte den Link aus der E-Mail benutzen.");
      return;
    }
    laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Eine Aktion abschicken und dabei sagen, wenn sie nicht angenommen wurde.
   * Vorher lief jede Antwort ins Leere: bei einem abgelehnten Klick tat die
   * Seite einfach nichts, und der Kunde stand ratlos davor.
   */
  async function senden(pfad: string, koerper: unknown) {
    setLaeuft(true);
    setHinweis(null);
    try {
      const r = await fetch(`/v1/status/${encodeURIComponent(token)}/${pfad}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(koerper),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        setHinweis(d?.message ?? "Das hat gerade nicht geklappt. Bitte laden Sie die Seite neu.");
      }
    } catch {
      setHinweis("Keine Verbindung. Bitte prüfen Sie Ihr Internet und versuchen es noch einmal.");
    }
    laden();
    setLaeuft(false);
  }

  const terminWaehlen = (terminId: string) => senden("termin", { terminId });
  const terminAbsagen = (terminId: string, grund: string) => senden("termin-absagen", { terminId, grund });
  const keinerPasst = (hinweis: string) => senden("termine-passen-nicht", { hinweis });
  const angebotAntwort = (dokumentId: string, antwort: "annehmen" | "ablehnen") =>
    senden("angebot", { dokumentId, antwort });

  if (fehler) {
    return (
      <Rahmen>
        <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
          <span>{fehler}</span>
        </div>
      </Rahmen>
    );
  }
  if (!daten) return <Rahmen><p className="text-slate-500">Einen Moment …</p></Rahmen>;

  const { anfrage: a, company: c } = daten;
  const farbe = c.primary_color || "#1a1a2e";
  // "Angebot abgelehnt" ist kein Zeitstrahl-Schritt, sondern eine Abzweigung
  // beim Angebot - für die Anzeige auf die Höhe "Angebot verschickt" setzen
  // und darunter einen Hinweis zeigen.
  const abgelehnt = a.status === "quote_declined";
  const abgesagt = a.status === "cancelled";
  // Die Liste kommt neueste zuerst - das erste nicht stornierte Angebot ist
  // das gültige.
  const neuestesAngebot = (daten.dokumente ?? [])
    .find((d) => d.art === "angebot" && !d.storniert_am)?.id;
  const statusFuerStrahl = abgelehnt ? "quote_sent" : abgesagt ? "received" : a.status;
  // Unbekannter Status: lieber alles als erledigt zeigen als zurückspringen.
  const jetzt = STUFEN.findIndex((s) => s.id === statusFuerStrahl);
  const aktuell = jetzt >= 0 ? jetzt : STUFEN.length - 1;

  return (
    <Rahmen>
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: farbe }}>
          {c.name}
        </p>
        <h1 className="mt-1 text-xl font-semibold text-slate-900">
          Ihr Auftrag {a.referenceNumber}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {a.subService || a.serviceCategory} · eingegangen am {datum(a.createdAt)}
        </p>
      </div>

      {abgesagt && (
        <div className="mb-6 rounded-xl border border-slate-300 bg-slate-100 p-4 text-sm text-slate-700">
          Diese Anfrage wurde abgesagt. Details stehen in der E-Mail, die Sie
          von uns erhalten haben{c.phone ? ` – bei Fragen erreichen Sie uns unter ${c.phone}` : ""}.
        </div>
      )}

      {hinweis && (
        <div className="mb-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
          <span>{hinweis}</span>
        </div>
      )}

      {abgelehnt && (
        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          Sie haben das Angebot abgelehnt. Wenn sich etwas geändert hat, melden
          Sie sich einfach{c.phone ? ` unter ${c.phone}` : ""} – wir erstellen
          Ihnen gerne ein neues.
        </div>
      )}

      {/* Zu erledigen: Termin wählen bzw. Angebot beantworten. */}
      <TerminBlock termine={daten.termine ?? []} farbe={farbe} laeuft={laeuft} waehlen={terminWaehlen} absagen={terminAbsagen} keinerPasst={keinerPasst} />
      {(daten.dokumente ?? []).map((d) => (
        <DokumentBlock
          key={d.id}
          dok={d}
          farbe={farbe}
          laeuft={laeuft}
          antworten={angebotAntwort}
          token={token}
          /* Nur das jüngste Angebot ist noch zu beantworten - sonst nimmt der
             Kunde versehentlich einen überholten Preis an. */
          veraltet={d.art === "angebot" && d.id !== neuestesAngebot}
        />
      ))}

      {!abgesagt && <NachrichtBlock token={token} farbe={farbe} />}

      {/* Vom Betrieb hochgeladene PDF-Dateien - z.B. die Rechnung aus der
          Buchhaltung. Die Bestätigungs-Mails verweisen ausdrücklich hierauf. */}
      {(daten.documents ?? []).length > 0 && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Ihre Unterlagen
          </p>
          <div className="space-y-2">
            {(daten.documents ?? []).map((f) => (
              <a
                key={f.id}
                href={`/v1/status/${encodeURIComponent(token)}/files/${f.id}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3 text-sm hover:bg-slate-50"
              >
                <span>
                  <span className="block font-medium text-slate-900">
                    {DOKUMENT_NAME[f.doc_type] ?? "Dokument"}
                  </span>
                  <span className="text-xs text-slate-500">{f.filename}</span>
                </span>
                <Download className="h-4 w-4 flex-none" style={{ color: farbe }} />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Bewertung: erst wenn die Arbeit erledigt ist, und nur einmal. */}
      {(a.status === "completed" || a.status === "invoiced") && (
        <BewertungBlock
          token={token}
          farbe={farbe}
          vorhanden={daten.rating ?? null}
          nachher={laden}
          oeffentlicherLink={c.bewertung_link ?? null}
        />
      )}

      <ol className="relative space-y-0 border-l border-slate-200 pl-6">
        {STUFEN.map((s, i) => {
          const erledigt = i < aktuell;
          const hier = i === aktuell;
          return (
            <li key={s.id} className="relative pb-6 last:pb-0">
              <span
                className="absolute -left-[31px] flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white"
                style={{
                  background: erledigt || hier ? farbe : "#e2e8f0",
                  color: "#fff",
                }}
              >
                {erledigt ? <Check className="h-3 w-3" /> : hier ? <Clock className="h-3 w-3" /> : null}
              </span>
              <p className={hier ? "font-semibold text-slate-900" : "text-slate-500"}>{s.label}</p>
              {hier && <p className="mt-0.5 text-sm text-slate-500">{s.beschreibung}</p>}
              {hier && s.id === "inspection_scheduled" && a.inspectionDate && (
                <p className="mt-1 text-sm font-medium" style={{ color: farbe }}>
                  Termin: {datum(a.inspectionDate)}
                </p>
              )}
              {hier && s.id === "scheduled" && a.scheduledDate && (
                <p className="mt-1 text-sm font-medium" style={{ color: farbe }}>
                  Termin: {datum(a.scheduledDate)}
                  {a.assignedTechnician ? ` · ${a.assignedTechnician}` : ""}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {(a.description || a.customerStrasse) && (
        <div className="mt-6 rounded-xl bg-slate-50 p-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Ihre Angaben
          </p>
          {a.description && (
            <p className="text-sm leading-relaxed text-slate-700">{a.description}</p>
          )}
          {/* Die Anschrift steht hier, damit der Kunde einen Zahlendreher
              bemerkt, bevor der Betrieb vor der falschen Tür steht. */}
          {a.customerStrasse && (
            <p className="mt-2 text-sm text-slate-500">
              Einsatzort: {[a.customerStrasse, a.customerPlz].filter(Boolean).join(", ")}
              <span className="block text-xs text-slate-400">
                Stimmt etwas nicht? Schreiben Sie uns unten kurz eine Nachricht.
              </span>
            </p>
          )}
        </div>
      )}

      {c.phone && (
        <a
          href={`tel:${c.phone.replace(/[^0-9+]/g, "")}`}
          className="mt-6 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white"
          style={{ background: farbe }}
        >
          <Phone className="h-4 w-4" /> {c.phone}
        </a>
      )}
    </Rahmen>
  );
}

/**
 * Frage an den Betrieb - direkt von der Verfolgungsseite. Der Endpunkt
 * existierte längst, es fehlte nur die Oberfläche: Kunden mussten anrufen.
 */
function NachrichtBlock({ token, farbe }: { token: string; farbe: string }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [gesendet, setGesendet] = useState(false);

  async function senden() {
    if (!text.trim()) return;
    setLaeuft(true);
    const r = await fetch(`/v1/status/${encodeURIComponent(token)}/message`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: text.trim() }),
    });
    setLaeuft(false);
    if (r.ok) { setGesendet(true); setText(""); }
  }

  if (gesendet) {
    return (
      <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
        Ihre Nachricht ist angekommen – wir melden uns so schnell wie möglich.
      </div>
    );
  }
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
      {!offen ? (
        <button
          className="text-sm font-medium hover:underline"
          style={{ color: farbe }}
          onClick={() => setOffen(true)}
        >
          Eine Frage zu Ihrem Auftrag? Nachricht schreiben →
        </button>
      ) : (
        <>
          <p className="mb-2 text-sm font-semibold text-slate-900">Nachricht an uns</p>
          <textarea
            className="mb-2 w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-slate-400"
            rows={3}
            placeholder="Ihre Frage oder Anmerkung …"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button
            className="rounded-xl px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60"
            style={{ background: farbe }}
            disabled={laeuft || !text.trim()}
            onClick={senden}
          >
            {laeuft ? "Wird gesendet …" : "Nachricht senden"}
          </button>
        </>
      )}
    </div>
  );
}

function Rahmen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-7 shadow-elevated">
        {children}
      </div>
    </div>
  );
}

/** Terminvorschläge: der Kunde tippt auf den Termin, der ihm passt. */
function TerminBlock({
  termine, farbe, laeuft, waehlen, absagen, keinerPasst,
}: {
  termine: Termin[]; farbe: string; laeuft: boolean;
  waehlen: (id: string) => void; absagen: (id: string, grund: string) => void;
  keinerPasst: (hinweis: string) => void;
}) {
  const [absageOffen, setAbsageOffen] = useState(false);
  const [grund, setGrund] = useState("");
  const [passtNichtOffen, setPasstNichtOffen] = useState(false);
  const [hinweis, setHinweis] = useState("");
  // Abgesagte Vorschläge sind erledigt - sie sollen die Auswahl nicht zumüllen.
  // Genauso Vorschläge, deren Zeitpunkt vorbei ist: einen Termin von gestern
  // kann niemand mehr wahrnehmen.
  const jetzt = Date.now();
  const vorbei = (t: Termin) => new Date(t.beginnt_am).getTime() < jetzt;
  const offene = termine.filter((t) => !t.abgesagt_am && (t.gewaehlt || !vorbei(t)));
  if (!offene.length) return null;
  const gewaehlt = offene.find((t) => t.gewaehlt);
  // Ein gewählter Termin, der vorbei ist, war der Besuch - er darf nicht
  // weiter als "Ihr Termin steht" mit Absage-Knopf dastehen.
  const gewesen = Boolean(gewaehlt && vorbei(gewaehlt));
  if (gewaehlt && gewesen) {
    return (
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm font-semibold text-slate-900">Ihr Termin</p>
        <p className="mt-1 text-sm text-slate-600">
          {new Date(gewaehlt.beginnt_am).toLocaleString("de-DE", {
            weekday: "long", day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          })} Uhr · {gewaehlt.art === "besichtigung" ? "Besichtigung" : "Ausführung"}
        </p>
        <p className="mt-2 text-xs text-slate-400">
          Dieser Termin ist vorbei. Sobald es weitergeht, sehen Sie es hier.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl border p-4" style={{ borderColor: farbe + "40", background: farbe + "0a" }}>
      <p className="mb-1 text-sm font-semibold text-slate-900">
        {gewaehlt ? "Ihr Termin steht" : "Bitte wählen Sie einen Termin"}
      </p>
      {!gewaehlt && (
        <p className="mb-3 text-sm text-slate-500">
          Tippen Sie auf den Termin, der Ihnen am besten passt.
        </p>
      )}
      <div className="space-y-2">
        {offene.map((t) => {
          const dieser = t.gewaehlt;
          if (gewaehlt && !dieser) return null;
          return (
            <button
              key={t.id}
              disabled={laeuft || Boolean(gewaehlt)}
              onClick={() => waehlen(t.id)}
              className="flex w-full items-center justify-between rounded-lg border bg-white px-4 py-3 text-left text-sm transition disabled:cursor-default"
              style={{ borderColor: dieser ? farbe : "#e2e8f0" }}
            >
              <span>
                <span className="block font-medium text-slate-900">
                  {new Date(t.beginnt_am).toLocaleString("de-DE", {
                    weekday: "long", day: "2-digit", month: "2-digit",
                    hour: "2-digit", minute: "2-digit",
                  })} Uhr
                </span>
                <span className="text-xs text-slate-500">
                  ca. {t.dauer_minuten} Minuten · {t.art === "besichtigung" ? "Besichtigung" : "Ausführung"}
                </span>
              </span>
              {dieser
                ? <Check className="h-5 w-5 flex-none" style={{ color: farbe }} />
                : <span className="text-xs font-medium" style={{ color: farbe }}>auswählen</span>}
            </button>
          );
        })}
      </div>

      {!gewaehlt && !passtNichtOffen && (
        <button
          className="mt-3 text-xs text-slate-500 underline hover:text-slate-700"
          onClick={() => setPasstNichtOffen(true)}
          disabled={laeuft}
        >
          Keiner dieser Termine passt?
        </button>
      )}
      {!gewaehlt && passtNichtOffen && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
          <p className="mb-2 text-sm text-slate-700">
            Kein Problem – sagen Sie uns kurz, wann es besser passt. Der
            Betrieb schlägt dann neue Termine vor.
          </p>
          <textarea
            className="mb-2 w-full rounded-lg border border-slate-200 p-2 text-sm"
            rows={2}
            placeholder="z.B. „nur nachmittags“ oder „erst ab nächster Woche“"
            value={hinweis}
            onChange={(e) => setHinweis(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              style={{ background: farbe }}
              disabled={laeuft}
              onClick={() => keinerPasst(hinweis)}
            >
              Abschicken
            </button>
            <button
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600"
              onClick={() => { setPasstNichtOffen(false); setHinweis(""); }}
              disabled={laeuft}
            >
              Zurück
            </button>
          </div>
        </div>
      )}

      {/* Absagen: lieber hier mit zwei Klicks als gar nicht - sonst steht der
          Betrieb umsonst vor der Tür. */}
      {gewaehlt && !absageOffen && (
        <button
          className="mt-3 text-xs text-slate-500 underline hover:text-slate-700"
          onClick={() => setAbsageOffen(true)}
          disabled={laeuft}
        >
          Termin doch absagen
        </button>
      )}
      {gewaehlt && absageOffen && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
          <p className="mb-2 text-sm text-slate-700">
            Termin absagen? Der Betrieb wird sofort benachrichtigt und meldet
            sich mit neuen Terminvorschlägen.
          </p>
          <textarea
            className="mb-2 w-full rounded-lg border border-slate-200 p-2 text-sm"
            rows={2}
            placeholder="Grund (freiwillig) – z.B. „bin an dem Tag verhindert“"
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              style={{ background: "#e11d48" }}
              disabled={laeuft}
              onClick={() => absagen(gewaehlt.id, grund)}
            >
              Termin absagen
            </button>
            <button
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600"
              onClick={() => { setAbsageOffen(false); setGrund(""); }}
              disabled={laeuft}
            >
              Doch nicht
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Angebot (mit Zusage/Absage) oder Arbeitsbericht. */
function DokumentBlock({
  dok, farbe, laeuft, antworten, token, veraltet = false,
}: {
  dok: Dokument; farbe: string; laeuft: boolean;
  antworten: (id: string, a: "annehmen" | "ablehnen") => void;
  token: string;
  veraltet?: boolean;
}) {
  const istAngebot = dok.art === "angebot";
  const istRechnung = dok.art === "rechnung";
  const offen = istAngebot && !dok.angenommen_am && !dok.abgelehnt_am
    && !dok.storniert_am && !veraltet;

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: farbe }}>
            {dok.storno_zu ? "Stornorechnung" : istAngebot ? "Angebot" : istRechnung ? "Rechnung" : "Arbeitsbericht"} {dok.nummer}
          </p>
          <p className="font-semibold text-slate-900">{dok.titel}</p>
        </div>
        {dok.storniert_am && (
          <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
            storniert am {new Date(dok.storniert_am).toLocaleDateString("de-DE")}
          </span>
        )}
        {dok.angenommen_am && (
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            angenommen
          </span>
        )}
        {dok.abgelehnt_am && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            abgelehnt
          </span>
        )}
        {veraltet && !dok.angenommen_am && !dok.abgelehnt_am && !dok.storniert_am && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            durch neueres ersetzt
          </span>
        )}
      </div>

      {dok.text_oben && <p className="mb-3 text-sm leading-relaxed text-slate-600">{dok.text_oben}</p>}

      {dok.positionen.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {dok.positionen.map((p, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-2 text-slate-700">
                  {p.bezeichnung}
                  {/* Im Arbeitsbericht stehen keine Preise - dort zählt nur,
                      was gemacht wurde. */}
                  {(istAngebot || istRechnung) && (
                    <span className="block text-xs text-slate-400">
                      {p.menge} {p.einheit} × {EUR(p.einzelpreisCent)}
                    </span>
                  )}
                </td>
                <td className="py-2 text-right font-medium text-slate-900 whitespace-nowrap">
                  {istAngebot || istRechnung
                    ? EUR(Math.round(p.menge * p.einzelpreisCent))
                    : <span className="text-sm text-slate-400">{p.menge > 1 ? `${p.menge} ${p.einheit}` : "erledigt"}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(istAngebot || istRechnung) && dok.brutto_cent !== 0 && (
        <div className="mt-3 border-t border-slate-200 pt-3 text-sm">
          <div className="flex justify-between text-slate-500">
            <span>Netto</span><span>{EUR(dok.netto_cent)}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>zzgl. {dok.mwst_prozent} % MwSt.</span>
            <span>{EUR(dok.brutto_cent - dok.netto_cent)}</span>
          </div>
          <div className="mt-1 flex justify-between text-base font-semibold text-slate-900">
            <span>Gesamt</span><span style={{ color: farbe }}>{EUR(dok.brutto_cent)}</span>
          </div>
        </div>
      )}

      {dok.text_unten && <p className="mt-3 text-sm leading-relaxed text-slate-600">{dok.text_unten}</p>}

      {dok.gueltig_bis && offen && (
        <p className="mt-3 text-xs text-slate-400">
          Gültig bis {new Date(dok.gueltig_bis).toLocaleDateString("de-DE")}
        </p>
      )}

      {/* Zum Ausdrucken und Abheften. Die E-Rechnung weiter unten ist das
          gesetzliche Format für die Buchhaltung - lesen lässt sie sich nicht. */}
      <div className="mt-4 border-t border-slate-200 pt-3">
        <a
          href={`/v1/status/${encodeURIComponent(token)}/dokument/${dok.id}/pdf`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm font-medium underline-offset-2 hover:underline"
          style={{ color: farbe }}
        >
          <Download className="h-4 w-4" /> Als PDF öffnen oder speichern
        </a>
      </div>

      {istRechnung && (
        <div className="mt-3 space-y-2">
          {dok.storniert_am ? (
            <p className="text-sm text-slate-500">
              Diese Rechnung wurde storniert — aus ihr entsteht keine Zahlungspflicht.
            </p>
          ) : dok.faellig_am ? (
            <p className="text-sm font-medium text-slate-700">
              Zahlbar bis {new Date(dok.faellig_am).toLocaleDateString("de-DE")}
            </p>
          ) : null}
          {/* Die E-Rechnung im gesetzlichen Format (XRechnung) - für Firmenkunden
              und deren Buchhaltung. Privatkunden brauchen sie meist nicht. */}
          <a
            href={`/v1/status/${encodeURIComponent(token)}/erechnung/${dok.id}`}
            className="inline-flex items-center gap-2 text-sm font-medium underline-offset-2 hover:underline"
            style={{ color: farbe }}
          >
            <Download className="h-4 w-4" /> E-Rechnung herunterladen (XRechnung, XML)
          </a>
        </div>
      )}

      {offen && (
        <div className="mt-4 flex gap-2">
          <button
            disabled={laeuft}
            onClick={() => antworten(dok.id, "annehmen")}
            className="flex-1 rounded-xl px-4 py-2.5 text-sm font-medium text-white disabled:opacity-60"
            style={{ background: farbe }}
          >
            Angebot annehmen
          </button>
          <button
            disabled={laeuft}
            onClick={() => antworten(dok.id, "ablehnen")}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 disabled:opacity-60"
          >
            Ablehnen
          </button>
        </div>
      )}
    </div>
  );
}

/** Sternebewertung nach abgeschlossener Arbeit - einmal pro Auftrag. */
function BewertungBlock({
  token, farbe, vorhanden, nachher, oeffentlicherLink,
}: {
  token: string;
  farbe: string;
  vorhanden: { stars: number; comment: string | null } | null;
  nachher: () => void;
  oeffentlicherLink?: string | null;
}) {
  // Aus der Bewertungs-Mail kommt der Kunde mit ?sterne=N - dann ist die
  // Wertung schon gesetzt und es fehlt nur noch ein Klick.
  const ausLink = Number(new URLSearchParams(window.location.search).get("sterne"));
  const [sterne, setSterne] = useState(ausLink >= 1 && ausLink <= 5 ? ausLink : 0);
  const [schwebt, setSchwebt] = useState(0);
  const [kommentar, setKommentar] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (vorhanden) {
    // Zufriedene Kunden öffentlich sichtbar machen - genau daraus entsteht
    // Vertrauen bei den nächsten. Nur einladen, nie automatisch weiterleiten.
    const zufrieden = vorhanden.stars >= 4;
    return (
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 text-center">
        <div className="mb-1 flex justify-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              className="h-6 w-6"
              fill={n <= vorhanden.stars ? farbe : "none"}
              style={{ color: n <= vorhanden.stars ? farbe : "#cbd5e1" }}
            />
          ))}
        </div>
        <p className="text-sm text-slate-600">Vielen Dank für Ihre Bewertung!</p>
        {zufrieden && oeffentlicherLink && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <p className="mb-3 text-sm text-slate-600">
              Das freut uns sehr. Würden Sie das öffentlich teilen? Das hilft
              anderen bei der Suche nach einem Betrieb – und uns am meisten.
            </p>
            <a
              href={oeffentlicherLink}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-block rounded-xl px-5 py-2.5 text-sm font-medium text-white"
              style={{ background: farbe }}
            >
              Öffentlich bewerten
            </a>
          </div>
        )}
      </div>
    );
  }

  async function absenden() {
    if (!sterne) return;
    setLaeuft(true); setFehler(null);
    try {
      const res = await fetch(`/v1/status/${encodeURIComponent(token)}/rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stars: sterne, comment: kommentar.trim() || null }),
      });
      if (!res.ok) throw new Error(String(res.status));
      nachher();
    } catch {
      setFehler("Das hat nicht geklappt. Bitte noch einmal versuchen.");
      setLaeuft(false);
    }
  }

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
      <p className="mb-1 font-semibold text-slate-900">Wie zufrieden waren Sie?</p>
      <p className="mb-3 text-sm text-slate-500">
        Ihre Bewertung hilft dem Betrieb und dauert nur einen Moment.
      </p>
      <div className="mb-3 flex gap-1" onMouseLeave={() => setSchwebt(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`${n} Sterne`}
            onMouseEnter={() => setSchwebt(n)}
            onClick={() => setSterne(n)}
            className="rounded p-1 transition-transform hover:scale-110"
          >
            <Star
              className="h-8 w-8"
              fill={n <= (schwebt || sterne) ? farbe : "none"}
              style={{ color: n <= (schwebt || sterne) ? farbe : "#cbd5e1" }}
            />
          </button>
        ))}
      </div>
      {sterne > 0 && (
        <>
          <textarea
            className="mb-3 w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-slate-400"
            rows={3}
            placeholder="Möchten Sie noch etwas dazu sagen? (freiwillig)"
            value={kommentar}
            onChange={(e) => setKommentar(e.target.value)}
          />
          {fehler && (
            <p className="mb-3 flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-none" /> {fehler}
            </p>
          )}
          <button
            type="button"
            disabled={laeuft}
            onClick={absenden}
            className="rounded-xl px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60"
            style={{ background: farbe }}
          >
            {laeuft ? "Wird gesendet …" : "Bewertung absenden"}
          </button>
        </>
      )}
    </div>
  );
}
